
import { Component, Vue, Watch } from 'vue-property-decorator';
import QvInput from '@/components/FormComponents/QvInput.vue';
import QvSelect from '@/components/FormComponents/QvSelect.vue';
import { ValidationObserver } from 'vee-validate';
import {
  MapStore,
  FarmStore,
  SensorStore,
  UserStore,
  JwtStore
} from '@/store/modules';
import LocationInputMap from '@/components/Forms/Components/LocationInputMap.vue';
import { Farm } from '@/store/models/farm';
import { isSuperuser, roleChecker } from '@/utils/jwt';
import { UserTypes } from '@/store/models/user';
import { Coordinate } from 'ol/coordinate';
import { Geom } from '@/store/models/map';

@Component({
  components: {
    ValidationObserver,
    QvInput,
    QvSelect,
    LocationInputMap
  }
})
export default class FarmForm extends Vue {
  $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };

  allSelected: boolean | null = false;
  farmPosition: null | Coordinate = null;

  get UserTypes() {
    return UserTypes;
  }

  checkAll(value: boolean | null) {
    if (value) {
      this.farm.managerList = this.managerList
        .filter(option => option.value != null)
        .map(option => option.value as number);
      this.onManagerChange();
      return;
    }
    this.$set(this.farm, 'managerList', [] as number[]);
    this.onManagerChange();
  }

  checkVals(value: string[]) {
    this.onManagerChange();
    this.allSelected =
      value.length > 0 && value.length < this.managerList.length
        ? null
        : value.length === this.managerList.length
        ? true
        : false;
  }

  get currentUser() {
    return JwtStore.UserDetail;
  }

  get ownerList() {
    const userList: {
      value: number | null;
      label: string;
    }[] = UserStore.SimpleUserList.filter(x => x.role == UserTypes.OWNER).map(
      x => {
        return { value: x.id, label: x.fullName };
      }
    );
    if (
      this.farm.owner &&
      this.farm.ownerName &&
      !userList.find(x => x.value == this.farm.owner)
    ) {
      userList.unshift({
        value: this.farm.owner,
        label: this.farm.ownerName
      });
    }
    return userList;
  }

  get managerList() {
    let userList: {
      value: number | null;
      label: string;
      parentId?: number | null;
    }[] = UserStore.SimpleUserList.filter(x => x.role == UserTypes.MANAGER).map(
      x => {
        return { value: x.id, label: x.fullName, parentId: x.parentId };
      }
    );
    if (this.farm.owner) {
      userList = userList.filter(x => x.parentId == this.farm.owner);
      const owner = this.ownerList.find(x => x.value == this.farm.owner);

      if (owner) {
        userList.unshift({
          value: owner.value,
          label: owner.label
        });
      }
    }
    if (!userList.filter(x => x.value == this.currentUser.id)) {
      userList.unshift({
        value: this.currentUser.id,
        label: this.currentUser.fullName
      });
    }
    return userList;
  }

  get ownerDisabled() {
    if (isSuperuser()) {
      return false;
    }
    if (roleChecker([UserTypes.OWNER, UserTypes.MANAGER])) {
      return true;
    }
    return false;
  }

  get managerDisabled() {
    if (isSuperuser()) {
      return false;
    }
    if (roleChecker([UserTypes.MANAGER])) {
      return true;
    }
    return false;
  }

  get emptyFarm() {
    return FarmStore.emptyFarm;
  }
  get farm() {
    return JSON.parse(JSON.stringify(FarmStore.AddEditFarm));
  }

  set farm(farm: Farm) {
    FarmStore.setAddEditFarm(farm);
  }
  pointGeom: Geom | string = '';

  @Watch('pointGeom', { deep: true })
  onFarmChange(newVal: Geom | string) {
    if (newVal) {
      this.farm.pointGeom = newVal;
    }
  }

  get submitLabel() {
    if (this.farm && this.farm.id && this.farm.id != 0) {
      return 'Update';
    }
    return 'Add';
  }

  deleteDialog = false;

  get crops() {
    return FarmStore.Farms.map(x => {
      return { value: x.id, label: `${x.name}` };
    });
  }
  get sensors() {
    return SensorStore.Sensors.map(x => {
      return { value: x.id, label: x.name };
    });
  }

  setArea(areaSqM: number) {
    const areaHa = (areaSqM / 10000).toFixed(2);
    this.farm.area = Number(areaHa);
    FarmStore.setAddEditFarm(this.farm);
  }

  onManagerChange() {
    FarmStore.setAddEditFarm(this.farm);
  }

  async onSubmit() {
    this.farm.pointGeom = this.pointGeom;
    if (this.farm.id && this.farm.id != 0) {
      await FarmStore.updateFarm(this.farm);
      this.$router.push({ name: 'FarmList' });
      this.$q.notify({
        message: 'Farm updated successfully',
        type: 'positive'
      });
    } else {
      await FarmStore.createFarm(this.farm);
      this.$router.push({ name: 'FarmList' });
      // this.$router.replace({
      //   name: 'EditFarm',
      //   params: { farmId: String(this.farm.id) }
      // });
      this.$q.notify({
        message: 'Farm added successfully',
        type: 'positive'
      });
    }
    MapStore.setUpdateMapAlbums(true);
  }

  onReset() {
    requestAnimationFrame(() => {
      this.farm = JSON.parse(JSON.stringify(this.emptyFarm));
      this.$refs.observer.reset();
      MapStore.updateMapSize();

      setTimeout(() => {
        MapStore.stopUpdateAnimation();
      }, 600);
      this.$router.push({ name: 'FarmList' });
    });
  }

  async onDelete() {
    this.deleteDialog = false;
    if (this.farm && this.farm.id && this.farm.id != 0) {
      FarmStore.deleteFarm(this.farm.id);
      this.$router.push({ name: 'FarmList' });

      const layer = await MapStore.getVectorLayer('Farm Layer');
      if (layer) {
        const feature = layer.getSource().getFeatureById(this.farm.id);
        if (feature) {
          layer.getSource().removeFeature(feature);
        }
      }
    }
  }

  deleteFarm() {
    this.deleteDialog = true;
  }

  get farms() {
    return FarmStore.Farms;
  }

  async created() {
    this.farm = JSON.parse(JSON.stringify(this.emptyFarm));
    await UserStore.getSimpleUsersList();
    if (this.$route.params.farmId) {
      this.allSelected = null;
      const farmId = Number(this.$route.params.farmId);
      await FarmStore.getFarmById(farmId);
      this.pointGeom = this.farm.pointGeom;
      if (typeof this.farm.pointGeom != 'string') {
        this.farmPosition = this.farm.pointGeom.coordinates as Coordinate;
      }
    } else {
      if (roleChecker([UserTypes.OWNER])) {
        this.farm.owner = this.currentUser.id;
        this.farm.managerList = [this.currentUser.id];
      } else if (roleChecker([UserTypes.MANAGER]) && this.currentUser.parent) {
        this.farm.owner = this.currentUser.parent.parent;
        this.farm.managerList = [this.currentUser.id];
      }
    }
    FarmStore.setAddEditFarm(this.farm);
  }
}
